import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { joinWaitlist, getWaitlistCount } from "@/lib/api";

const ROLES = ["AI Agent Builder", "dApp Founder", "Ecosystem Protocol", "Investor / Researcher"];

export default function Waitlist() {
    const [role, setRole] = useState(ROLES[0]);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [count, setCount] = useState(null);

    useEffect(() => {
        getWaitlistCount()
            .then(setCount)
            .catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            toast.error("Enter a valid email address");
            return;
        }
        setLoading(true);
        try {
            const res = await joinWaitlist(email.trim(), role);
            setResult(res);
            setCount((c) => (res.already_joined ? c : (c || 0) + 1));
            toast.success(res.already_joined ? "You're already on the list" : "Welcome aboard — you're in");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong — try again");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="waitlist"
            className="relative scroll-mt-24 overflow-hidden border-t border-neon-cyan/20 bg-gradient-to-b from-[#080B12] to-void px-4 py-24 sm:px-6 lg:px-8 lg:py-28"
        >
            <div className="pointer-events-none absolute left-1/2 top-0 h-[380px] w-[680px] -translate-x-1/2 rounded-full bg-neon-cyan/10 blur-[140px]" />
            <div className="relative mx-auto max-w-3xl text-center">
                <Reveal>
                    <Eyebrow className="justify-center">Early access</Eyebrow>
                    <h2 className="mt-4 font-display text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl">
                        Get your agents verified first.
                    </h2>
                    <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
                        SUID mainnet is opening in waves. Join the waitlist and be first in line when your
                        ecosystem goes live.
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-10 flex flex-wrap justify-center gap-2" data-testid="waitlist-role-pills">
                        {ROLES.map((r) => (
                            <button
                                key={r}
                                data-testid={`waitlist-role-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                onClick={() => setRole(r)}
                                className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] transition-all duration-300 ${
                                    role === r
                                        ? "border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan"
                                        : "border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200"
                                }`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        {result ? (
                            <motion.div
                                key="success"
                                data-testid="waitlist-success"
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                className="mx-auto mt-8 max-w-md rounded-2xl border border-neon-emerald/30 bg-neon-emerald/5 p-8"
                            >
                                <PartyPopper size={26} className="mx-auto text-neon-emerald" />
                                <p className="mt-4 font-display text-4xl font-extrabold text-white">
                                    #{result.position}
                                </p>
                                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon-emerald">
                                    {result.already_joined ? "Already on the list" : "You're on the list"}
                                </p>
                                <p className="mt-3 text-xs leading-relaxed text-slate-400">
                                    We'll email {email.trim() || "you"} when your wave opens. Watch your inbox.
                                </p>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                onSubmit={submit}
                                data-testid="waitlist-form"
                                exit={{ opacity: 0, y: -12 }}
                                className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:flex-row"
                            >
                                <input
                                    type="email"
                                    data-testid="waitlist-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@protocol.xyz"
                                    className="h-12 flex-1 rounded-lg border border-white/10 bg-white/[0.04] px-4 font-mono text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-neon-cyan/50"
                                />
                                <button
                                    type="submit"
                                    data-testid="waitlist-submit"
                                    disabled={loading}
                                    className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-neon-cyan px-6 font-mono text-xs font-bold uppercase tracking-[0.14em] text-void transition-all duration-300 hover:shadow-[0_0_32px_rgba(0,240,255,0.45)] disabled:opacity-60"
                                >
                                    {loading ? "Joining…" : "Join waitlist"}
                                    <ArrowRight size={14} />
                                </button>
                            </motion.form>
                        )}
                    </AnimatePresence>

                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        {count !== null ? `${count} builders in line · ` : ""}No spam · Mainnet soon
                    </p>
                </Reveal>
            </div>
        </section>
    );
}